import 'maplibre-gl/dist/maplibre-gl.css';

import maplibregl, {
  type FilterSpecification,
  type GeoJSONSource,
  type Map,
  type MapMouseEvent,
  type VectorTileSource,
} from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';
import type { FeatureCollection, LineString, Point } from 'geojson';

import type { BarrierDraft } from '@/stores/BarrierWorkspaceContext';
import type { MapLayerState } from '@/stores/MapLayersContext';
import type { MapToolMode } from '@/stores/MapToolContext';
import type { RouteDraft } from '@/stores/RouteWorkspaceContext';
import type { LatLonPoint } from '@/types/barriers';
import { ROUTE_VARIANT_KEYS, type RouteVariantKey } from '@/types/routes';
import { loadMapView, saveMapView } from '@/utils/mapView';

import {
  BARRIERS_ICON_HOVERED_LAYER_ID,
  BARRIERS_ICON_SELECTED_LAYER_ID,
  BARRIERS_SOURCE_ID,
  BARRIERS_ZONE_HOVERED_LAYER_ID,
  BARRIERS_ZONE_RESIDUAL_LAYER_ID,
  BARRIERS_ZONE_SELECTED_LAYER_ID,
  buildEmptyFilter,
  buildResidualFilter,
  buildSelectedFilter,
  buildTilesUrl,
} from './barriersLayer';
import {
  DETOUR_WAYPOINTS_HOVERED_LAYER_ID,
  DETOUR_WAYPOINTS_SOURCE_ID,
  type DetourWaypointProperties,
} from './detourWaypointsLayer';
import {
  INDUSTRIAL_REF_BOOK_HOVERED_LAYER_ID,
  INDUSTRIAL_REF_BOOK_LAYER_ID,
} from './industrialRefBookLayer';
import { FREIGHT_RESTRICTIONS_HOVER_LAYER_ID } from './freightRestrictionsLayer';
import {
  DRAFT_SOURCE_ID,
  buildDraftCollection,
} from './draftLayer';
import {
  ROUTE_DRAFT_SOURCE_ID,
  buildRouteDraftCollection,
} from './routeDraftLayer';
import {
  VARIANT_LAYER_SPECS,
  buildFocusedFilter,
} from './routesGeoJsonLayer';
import {
  applyMapLayerState,
  installMapSources,
  queryMapHit,
  type MapHitResult,
} from './mapInstallers';
import { BARRIER_ICON_IMAGE_ID, loadBarrierIconImage } from './barrierIcon';
import { GREEN_FLAG_ICON_IMAGE_ID, loadGreenFlagIconImage } from './greenFlagIcon';
import { WAYPOINT_ICON_IMAGE_ID, loadWaypointIconImage } from './waypointIcon';
import {
  WAYPOINT_OUTLINE_ICON_IMAGE_ID,
  loadWaypointOutlineIconImage,
} from './waypointOutlineIcon';
import {
  BARRIER_SELECTED_WAYPOINTS_SOURCE_ID,
  type BarrierSelectedWaypointProperties,
} from './barrierSelectedWaypointsLayer';

const BASEMAP_STYLE = 'https://tiles.openfreemap.org/styles/liberty';
const INITIAL_CENTER: [number, number] = [37.6173, 55.7558];
const INITIAL_ZOOM = 5;
const MIN_ZOOM = 3;
const MAX_ZOOM = 18;
const FIT_BOUNDS_PADDING_PX = 60;
const FIT_BOUNDS_MAX_ZOOM = 14;
const WAYPOINT_FLY_ZOOM = 17;

const CONTAINER_STYLE = { width: '100%', height: '100%' } as const;

export interface MapClickEvent {
  readonly point: LatLonPoint;
  readonly barrierId: number | null;
  readonly routeId: string | null;
  readonly routeVariant: RouteVariantKey | null;
  readonly detourBarrierId: number | null;
  readonly industrialRefBookId: number | null;
  readonly freightRestrictionWayId: number | null;
}

export type RouteBbox = readonly [number, number, number, number];

export type VariantCollections = Readonly<
  Record<RouteVariantKey, FeatureCollection<LineString>>
>;

export interface MainMapProps {
  readonly apiBaseUrl: string;
  readonly barriersRevision: number;
  readonly selectedBarrierId: number | null;
  readonly focusedRouteId: string | null;
  readonly focusedRouteBbox: RouteBbox | null;
  readonly focusedBarrierBbox: RouteBbox | null;
  readonly focusedWaypoint: Readonly<{
    lat: number;
    lon: number;
    token: number;
  }> | null;
  readonly residualBarrierIds: readonly number[];
  readonly routeCollections: VariantCollections;
  readonly detourCollection: FeatureCollection<Point, DetourWaypointProperties>;
  readonly barrierSelectedWaypoints: FeatureCollection<
    Point,
    BarrierSelectedWaypointProperties
  >;
  readonly barrierDraft: BarrierDraft | null;
  readonly routeDraft: RouteDraft | null;
  readonly mapLayers: readonly MapLayerState[];
  readonly mode: MapToolMode;
  /** Whether the map container is currently shown. The map stays mounted
   * across tab switches (to preserve camera/state); when it becomes visible
   * again after being hidden the canvas is resized to the container. */
  readonly visible: boolean;
  readonly onMapClick: (event: MapClickEvent) => void;
}

export default function MainMap({
  apiBaseUrl,
  barriersRevision,
  selectedBarrierId,
  focusedRouteId,
  focusedRouteBbox,
  focusedBarrierBbox,
  focusedWaypoint,
  residualBarrierIds,
  routeCollections,
  detourCollection,
  barrierSelectedWaypoints,
  barrierDraft,
  routeDraft,
  mapLayers,
  mode,
  visible,
  onMapClick,
}: MainMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<Map | null>(null);
  const tooltipRef = useRef<maplibregl.Popup | null>(null);
  // `mapLoaded` flips to true once sources + layers are installed on
  // map.on('load'). All dependent effects watch it so the *initial*
  // render of variant sources, legend state, selected-barrier filters
  // etc. runs as soon as the map is ready — not only on the next dep
  // change. A ref alone would not trigger re-renders.
  const [mapLoaded, setMapLoaded] = useState(false);
  const onMapClickRef = useRef(onMapClick);
  // Legend order drives click/hover priority. Read via ref so we don't
  // rebind listeners on every toggle or reorder — identify-tool lookups
  // always use the latest legend state at the moment of the event.
  const mapLayersRef = useRef(mapLayers);

  useEffect(() => {
    onMapClickRef.current = onMapClick;
  }, [onMapClick]);

  useEffect(() => {
    mapLayersRef.current = mapLayers;
  }, [mapLayers]);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    let cancelled = false;

    // Restore the last view across full reloads (F5); falls back to the
    // default Moscow framing on first visit or a corrupted value.
    const savedView = loadMapView();
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: BASEMAP_STYLE,
      center: savedView?.center ?? INITIAL_CENTER,
      zoom: savedView?.zoom ?? INITIAL_ZOOM,
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
    });
    map.addControl(new maplibregl.NavigationControl(), 'top-right');
    map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left');

    const handleMoveEnd = () => {
      const c = map.getCenter();
      saveMapView([c.lng, c.lat], map.getZoom());
    };
    map.on('moveend', handleMoveEnd);

    const handleClick = (event: MapMouseEvent) => {
      const hit = queryMapHit(map, event, mapLayersRef.current);
      onMapClickRef.current({
        point: { lat: event.lngLat.lat, lon: event.lngLat.lng },
        barrierId: hit.barrierId,
        routeId: hit.routeId,
        routeVariant: hit.routeVariant,
        detourBarrierId: hit.detourBarrierId,
        industrialRefBookId: hit.industrialRefBookId,
        freightRestrictionWayId: hit.freightRestrictionWayId,
      });
    };
    map.on('click', handleClick);

    map.on('load', () => {
      // Load every SVG icon in parallel; `settled` lets the map come up
      // even if one encode/decode cycle stalls — the symbol layer for
      // that icon simply won't render its image (MapLibre skips missing
      // `icon-image`s silently). Each icon is logged individually.
      Promise.allSettled([
        loadBarrierIconImage(),
        loadWaypointIconImage(),
        loadWaypointOutlineIconImage(),
        loadGreenFlagIconImage(),
      ]).then((results) => {
        if (cancelled) return;
        _applyIconResult(map, BARRIER_ICON_IMAGE_ID, results[0], 'barrier');
        _applyIconResult(map, WAYPOINT_ICON_IMAGE_ID, results[1], 'waypoint');
        _applyIconResult(
          map,
          WAYPOINT_OUTLINE_ICON_IMAGE_ID,
          results[2],
          'waypoint-outline',
        );
        _applyIconResult(
          map,
          GREEN_FLAG_ICON_IMAGE_ID,
          results[3],
          'green-flag',
        );
        installMapSources(map, apiBaseUrl, barriersRevision);
        setMapLoaded(true);
      });
    });

    mapRef.current = map;
    return () => {
      cancelled = true;
      setMapLoaded(false);
      map.remove();
      mapRef.current = null;
    };
  }, [apiBaseUrl]);

  // Map stays mounted across tab switches; when shown again after being
  // hidden (display:none zeroes the container) resize the canvas to fit.
  // Camera (center/zoom) is preserved across the hide/show.
  useEffect(() => {
    if (visible && mapLoaded) {
      mapRef.current?.resize();
    }
  }, [visible, mapLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const src = map.getSource(BARRIERS_SOURCE_ID);
    if (src && src.type === 'vector') {
      (src as VectorTileSource).setTiles([buildTilesUrl(apiBaseUrl, barriersRevision)]);
    }
  }, [apiBaseUrl, barriersRevision, mapLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    _setBarrierSelection(map, selectedBarrierId);
  }, [selectedBarrierId, mapLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    for (const variant of ROUTE_VARIANT_KEYS) {
      const id = VARIANT_LAYER_SPECS[variant].highlightLayerId;
      if (map.getLayer(id)) {
        map.setFilter(id, buildFocusedFilter(focusedRouteId));
      }
    }
  }, [focusedRouteId, mapLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    if (map.getLayer(BARRIERS_ZONE_RESIDUAL_LAYER_ID)) {
      map.setFilter(
        BARRIERS_ZONE_RESIDUAL_LAYER_ID,
        buildResidualFilter(residualBarrierIds),
      );
    }
  }, [residualBarrierIds, mapLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || focusedRouteBbox === null) return;
    const [west, south, east, north] = focusedRouteBbox;
    map.fitBounds(
      [[west, south], [east, north]],
      { padding: FIT_BOUNDS_PADDING_PX, maxZoom: FIT_BOUNDS_MAX_ZOOM, duration: 600 },
    );
  }, [focusedRouteBbox, mapLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || focusedBarrierBbox === null) return;
    const [west, south, east, north] = focusedBarrierBbox;
    map.fitBounds(
      [[west, south], [east, north]],
      { padding: FIT_BOUNDS_PADDING_PX, maxZoom: FIT_BOUNDS_MAX_ZOOM, duration: 600 },
    );
  }, [focusedBarrierBbox, mapLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || focusedWaypoint === null) return;
    // `flyTo` (vs `fitBounds` of a tiny synthetic bbox) lets us pick an
    // explicit zoom — important because waypoints are point features
    // and `fitBounds` would degenerate to maxZoom anyway, but with a
    // less predictable framing.
    map.flyTo({
      center: [focusedWaypoint.lon, focusedWaypoint.lat],
      zoom: WAYPOINT_FLY_ZOOM,
      duration: 600,
    });
  }, [focusedWaypoint, mapLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    for (const variant of ROUTE_VARIANT_KEYS) {
      const spec = VARIANT_LAYER_SPECS[variant];
      const src = map.getSource(spec.sourceId);
      if (src && src.type === 'geojson') {
        (src as GeoJSONSource).setData(routeCollections[variant]);
      }
    }
  }, [routeCollections, mapLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const src = map.getSource(DETOUR_WAYPOINTS_SOURCE_ID);
    if (src && src.type === 'geojson') {
      (src as GeoJSONSource).setData(detourCollection);
    }
  }, [detourCollection, mapLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const src = map.getSource(BARRIER_SELECTED_WAYPOINTS_SOURCE_ID);
    if (src && src.type === 'geojson') {
      (src as GeoJSONSource).setData(barrierSelectedWaypoints);
    }
  }, [barrierSelectedWaypoints, mapLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    applyMapLayerState(map, mapLayers);
  }, [mapLayers, mapLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const src = map.getSource(DRAFT_SOURCE_ID);
    if (src && src.type === 'geojson') {
      (src as GeoJSONSource).setData(buildDraftCollection(barrierDraft));
    }
  }, [barrierDraft, mapLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const src = map.getSource(ROUTE_DRAFT_SOURCE_ID);
    if (src && src.type === 'geojson') {
      (src as GeoJSONSource).setData(buildRouteDraftCollection(routeDraft));
    }
  }, [routeDraft, mapLoaded]);

  // Background cursor for the active map tool. The hover handlers
  // below temporarily switch to 'pointer' over interactable features
  // and revert to this base cursor on mouseleave.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    map.getCanvas().style.cursor = _cursorForMode(mode);
  }, [mode, mapLoaded]);

  // Single hover dispatcher driven by `queryMapHit` — top-first per
  // legend z-order. Compared with the previous per-layer
  // mouseenter/leave handlers, this guarantees that overlapping
  // features only highlight the one the user actually sees on top.
  // `mode` is read via a ref so we don't rebind handlers on every
  // mode change.
  const modeRef = useRef(mode);
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    let lastHover: HoverState | null = null;

    const onMove = (event: maplibregl.MapMouseEvent) => {
      // Pan mode keeps the standard MapLibre grab affordance.
      if (modeRef.current === 'pan') {
        if (lastHover !== null) {
          _applyHover(map, tooltipRef, null);
          lastHover = null;
        }
        return;
      }
      const hit = queryMapHit(map, event, mapLayersRef.current);
      const next = _hoverFromHit(map, event, hit);
      if (_sameHover(next, lastHover)) return;
      _applyHover(map, tooltipRef, next);
      lastHover = next;
      map.getCanvas().style.cursor =
        next !== null ? 'pointer' : _cursorForMode(modeRef.current);
    };

    const onCanvasLeave = () => {
      if (lastHover !== null) {
        _applyHover(map, tooltipRef, null);
        lastHover = null;
      }
      map.getCanvas().style.cursor = _cursorForMode(modeRef.current);
    };

    map.on('mousemove', onMove);
    map.getCanvas().addEventListener('mouseleave', onCanvasLeave);
    return () => {
      map.off('mousemove', onMove);
      map.getCanvas().removeEventListener('mouseleave', onCanvasLeave);
      tooltipRef.current?.remove();
      tooltipRef.current = null;
    };
  }, [mapLoaded]);

  return <div ref={containerRef} style={CONTAINER_STYLE} />;
}

function _applyIconResult(
  map: Map,
  imageId: string,
  result: PromiseSettledResult<HTMLImageElement>,
  label: string,
): void {
  if (result.status === 'rejected') {
    console.error(`[MainMap] ${label} icon load failed`, result.reason);
    return;
  }
  if (!map.hasImage(imageId)) {
    map.addImage(imageId, result.value);
  }
}

function _cursorForMode(mode: MapToolMode): string {
  switch (mode) {
    case 'pan':
      return ''; // MapLibre default — `grab` while idle, `grabbing` while dragging.
    case 'select':
      return 'default';
    case 'create_barrier':
    case 'add_waypoint':
    case 'pick_direction_hint':
    case 'pick_route_origin':
    case 'pick_route_destination':
    case 'create_route':
      return 'crosshair';
  }
}

type HoverState =
  | { readonly kind: 'barrier'; readonly id: number }
  | { readonly kind: 'detour'; readonly barrierId: number }
  | {
      readonly kind: 'industrial';
      readonly id: number;
      readonly title: string;
      readonly address: string;
      readonly lng: number;
      readonly lat: number;
    }
  | {
      readonly kind: 'route';
      readonly variant: RouteVariantKey;
      readonly routeId: string;
    }
  | { readonly kind: 'freight_restriction'; readonly wayId: number };

function _hoverFromHit(
  map: Map,
  event: MapMouseEvent,
  hit: MapHitResult,
): HoverState | null {
  if (hit.barrierId !== null) {
    return { kind: 'barrier', id: hit.barrierId };
  }
  if (hit.detourBarrierId !== null) {
    return { kind: 'detour', barrierId: hit.detourBarrierId };
  }
  if (hit.industrialRefBookId !== null) {
    const features = map.queryRenderedFeatures(event.point, {
      layers: [INDUSTRIAL_REF_BOOK_LAYER_ID],
    });
    const props = features[0]?.properties ?? {};
    return {
      kind: 'industrial',
      id: hit.industrialRefBookId,
      title: typeof props.title === 'string' ? props.title : '',
      address: typeof props.address === 'string' ? props.address : '',
      lng: event.lngLat.lng,
      lat: event.lngLat.lat,
    };
  }
  if (hit.routeId !== null && hit.routeVariant !== null) {
    return { kind: 'route', variant: hit.routeVariant, routeId: hit.routeId };
  }
  if (hit.freightRestrictionWayId !== null) {
    return { kind: 'freight_restriction', wayId: hit.freightRestrictionWayId };
  }
  return null;
}

function _sameHover(a: HoverState | null, b: HoverState | null): boolean {
  if (a === null || b === null) return a === b;
  if (a.kind !== b.kind) return false;
  if (a.kind === 'barrier' && b.kind === 'barrier') return a.id === b.id;
  if (a.kind === 'detour' && b.kind === 'detour') {
    return a.barrierId === b.barrierId;
  }
  if (a.kind === 'industrial' && b.kind === 'industrial') return a.id === b.id;
  if (a.kind === 'route' && b.kind === 'route') {
    return a.variant === b.variant && a.routeId === b.routeId;
  }
  if (a.kind === 'freight_restriction' && b.kind === 'freight_restriction') {
    return a.wayId === b.wayId;
  }
  return false;
}

function _applyHover(
  map: Map,
  tooltipRef: React.MutableRefObject<maplibregl.Popup | null>,
  next: HoverState | null,
): void {
  // Reset every hover filter to its empty form, then re-apply only the
  // active hover. Diffing prev→next would be more code for the same
  // outcome — every hover transition touches at most two layer filters.
  _resetHoverFilters(map);
  if (next === null) {
    tooltipRef.current?.remove();
    tooltipRef.current = null;
    return;
  }
  _setHoverFilter(map, next);
  if (next.kind === 'industrial' && (next.title || next.address)) {
    _showTooltip(
      map,
      tooltipRef,
      [next.lng, next.lat],
      next.title,
      next.address,
    );
  } else {
    tooltipRef.current?.remove();
    tooltipRef.current = null;
  }
}

function _resetHoverFilters(map: Map): void {
  if (map.getLayer(BARRIERS_ICON_HOVERED_LAYER_ID)) {
    map.setFilter(BARRIERS_ICON_HOVERED_LAYER_ID, buildEmptyFilter('icon'));
  }
  if (map.getLayer(BARRIERS_ZONE_HOVERED_LAYER_ID)) {
    map.setFilter(BARRIERS_ZONE_HOVERED_LAYER_ID, buildEmptyFilter('zone'));
  }
  if (map.getLayer(DETOUR_WAYPOINTS_HOVERED_LAYER_ID)) {
    map.setFilter(
      DETOUR_WAYPOINTS_HOVERED_LAYER_ID,
      ['==', ['get', 'barrier_id'], -1],
    );
  }
  if (map.getLayer(INDUSTRIAL_REF_BOOK_HOVERED_LAYER_ID)) {
    map.setFilter(
      INDUSTRIAL_REF_BOOK_HOVERED_LAYER_ID,
      ['==', ['get', 'id'], -1],
    );
  }
  if (map.getLayer(FREIGHT_RESTRICTIONS_HOVER_LAYER_ID)) {
    map.setFilter(
      FREIGHT_RESTRICTIONS_HOVER_LAYER_ID,
      ['==', ['get', 'way_id'], -1],
    );
  }
  for (const variant of ROUTE_VARIANT_KEYS) {
    const spec = VARIANT_LAYER_SPECS[variant];
    if (map.getLayer(spec.hoveredLayerId)) {
      map.setFilter(spec.hoveredLayerId, buildFocusedFilter(null));
    }
  }
}

function _setHoverFilter(map: Map, state: HoverState): void {
  if (state.kind === 'barrier') {
    if (map.getLayer(BARRIERS_ICON_HOVERED_LAYER_ID)) {
      map.setFilter(BARRIERS_ICON_HOVERED_LAYER_ID, [
        'all',
        ['==', ['get', 'feature_type'], 'icon'],
        ['==', ['get', 'id'], state.id],
      ]);
    }
    if (map.getLayer(BARRIERS_ZONE_HOVERED_LAYER_ID)) {
      map.setFilter(BARRIERS_ZONE_HOVERED_LAYER_ID, [
        'all',
        ['==', ['get', 'feature_type'], 'zone'],
        ['==', ['get', 'id'], state.id],
      ]);
    }
    return;
  }
  if (state.kind === 'detour') {
    if (map.getLayer(DETOUR_WAYPOINTS_HOVERED_LAYER_ID)) {
      map.setFilter(
        DETOUR_WAYPOINTS_HOVERED_LAYER_ID,
        ['==', ['get', 'barrier_id'], state.barrierId],
      );
    }
    return;
  }
  if (state.kind === 'industrial') {
    if (map.getLayer(INDUSTRIAL_REF_BOOK_HOVERED_LAYER_ID)) {
      map.setFilter(
        INDUSTRIAL_REF_BOOK_HOVERED_LAYER_ID,
        ['==', ['get', 'id'], state.id],
      );
    }
    return;
  }
  if (state.kind === 'freight_restriction') {
    if (map.getLayer(FREIGHT_RESTRICTIONS_HOVER_LAYER_ID)) {
      map.setFilter(
        FREIGHT_RESTRICTIONS_HOVER_LAYER_ID,
        ['==', ['get', 'way_id'], state.wayId],
      );
    }
    return;
  }
  const spec = VARIANT_LAYER_SPECS[state.variant];
  if (map.getLayer(spec.hoveredLayerId)) {
    map.setFilter(spec.hoveredLayerId, ['==', ['get', 'route_id'], state.routeId]);
  }
}

function _showTooltip(
  map: Map,
  tooltipRef: React.MutableRefObject<maplibregl.Popup | null>,
  lngLat: maplibregl.LngLatLike,
  title: string,
  address: string,
): void {
  const html = `
    <div style="max-width:240px;font-size:12px;line-height:1.35">
      <div style="font-weight:600;margin-bottom:2px">${_escape(title)}</div>
      ${address ? `<div style="color:#595959">${_escape(address)}</div>` : ''}
    </div>
  `;
  if (tooltipRef.current) {
    tooltipRef.current.setLngLat(lngLat).setHTML(html);
    return;
  }
  const popup = new maplibregl.Popup({
    closeButton: false,
    closeOnClick: false,
    anchor: 'bottom',
    offset: 10,
    className: 'route-trucks-hover-tooltip',
  })
    .setLngLat(lngLat)
    .setHTML(html)
    .addTo(map);
  tooltipRef.current = popup;
}

function _escape(text: string): string {
  // Minimal HTML escape for tooltip content. The DB never stores HTML,
  // but data comes from a third-party reference book — defensive.
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function _setBarrierSelection(map: Map, selectedId: number | null): void {
  const zoneFilter: FilterSpecification = selectedId !== null
    ? buildSelectedFilter('zone', selectedId)
    : buildEmptyFilter('zone');
  const iconFilter: FilterSpecification = selectedId !== null
    ? buildSelectedFilter('icon', selectedId)
    : buildEmptyFilter('icon');
  if (map.getLayer(BARRIERS_ZONE_SELECTED_LAYER_ID)) {
    map.setFilter(BARRIERS_ZONE_SELECTED_LAYER_ID, zoneFilter);
  }
  if (map.getLayer(BARRIERS_ICON_SELECTED_LAYER_ID)) {
    map.setFilter(BARRIERS_ICON_SELECTED_LAYER_ID, iconFilter);
  }
}

